import { useEffect, useMemo, useRef, useState } from 'react';

import { useUrlSlug } from 'Utilities/hooks/useUrlSlug';

import { getBeneficiary } from '../../../../services/privateApis';
import { Beneficiary } from '../../payments/Payments.types';
import { isAllowedPageRouteForHooks } from '../utils/routeChecker';

export type UseGetBeneficiary = {
  accountNumber?: string;
  error?: Error | null;
  loading: boolean;
};
const accountNumberShownDigits = 4;

export const setStatusCode = (status?: number, statusCode?: React.MutableRefObject<number>) => {
  if (status && statusCode) {
    statusCode.current = status;
  }
};

export const useGetBeneficiary = (
  valuationRef?: number | string | null,
  isAllowedStatusToFetch = false,
): UseGetBeneficiary => {
  const [beneficiary, setBeneficiary] = useState<Beneficiary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const slug = useUrlSlug();
  const abortController = useMemo(() => new AbortController(), []);
  const isRouteFetchAllowed = isAllowedPageRouteForHooks({ hookName: 'useGetBeneficiary', slug });
  const cancelled = useRef(false);
  const statusCode = useRef<number>(-1);

  useEffect(() => () => {
    cancelled.current = true;
  }, []);

  useEffect(() => {
    const fetchBeneficiary = async () => {
      setLoading(true);
      try {
        if (!valuationRef) {
          return;
        }
        const beneficiaryResponse = await getBeneficiary(
          valuationRef,
          abortController,
          setStatusCode,
        );
        if (!cancelled?.current) {
          setBeneficiary(beneficiaryResponse);
        }
      } catch (err) {
        if (cancelled?.current) {
          return;
        }
        setError(err as Error);
        window?.Sentry?.captureException?.(err, {
          extra: { cancelled: cancelled?.current, isAllowedStatusToFetch, isRouteFetchAllowed, valuationRef },
          tags: { statusCode: statusCode.current?.toString() },
        });
      } finally {
        if (!cancelled?.current) {
          setLoading(false);
        }
      }
    };

    if (valuationRef && isAllowedStatusToFetch && isRouteFetchAllowed) {
      fetchBeneficiary();
    }
  }, [valuationRef, isAllowedStatusToFetch, isRouteFetchAllowed, cancelled, abortController]);

  return {
    accountNumber:
    beneficiary?.accountNumber
      ? beneficiary.accountNumber.substring(beneficiary.accountNumber.length - accountNumberShownDigits) : '',
    error,
    loading,
  };
};
